import React, { useState, useMemo } from 'react';

interface DiffResult {
  type: 'added' | 'removed' | 'unchanged';
  text: string;
  lineNumber?: number;
}

const ContentDiff: React.FC = () => {
  const [leftContent, setLeftContent] = useState('');
  const [rightContent, setRightContent] = useState('');
  const [diffResult, setDiffResult] = useState<DiffResult[]>([]);
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [ignoreWhitespace, setIgnoreWhitespace] = useState(true);
  const [ignoreCase, setIgnoreCase] = useState(false);

  // 计算最长公共子序列
  const computeLCS = (arr1: string[], arr2: string[]): string[] => {
    const m = arr1.length;
    const n = arr2.length;
    const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
    
    // 填充DP表
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (arr1[i - 1] === arr2[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }
    
    // 回溯构建LCS
    const lcs: string[] = [];
    let i = m, j = n;
    while (i > 0 && j > 0) {
      if (arr1[i - 1] === arr2[j - 1]) {
        lcs.unshift(arr1[i - 1]);
        i--;
        j--;
      } else if (dp[i - 1][j] > dp[i][j - 1]) {
        i--;
      } else {
        j--;
      }
    }
    
    return lcs;
  };

  // 计算文本差异
  const computeDiff = useMemo(() => {
    if (!leftContent.trim() && !rightContent.trim()) {
      return [];
    }

    const leftLines = leftContent.split('\n');
    const rightLines = rightContent.split('\n');

    // 如果忽略空白字符，先处理
    let processedLeft = leftLines;
    let processedRight = rightLines;

    if (ignoreWhitespace) {
      processedLeft = leftLines.map(line => line.trim()).filter(line => line.length > 0);
      processedRight = rightLines.map(line => line.trim()).filter(line => line.length > 0);
    }

    if (ignoreCase) {
      processedLeft = processedLeft.map(line => line.toLowerCase());
      processedRight = processedRight.map(line => line.toLowerCase());
    }

    // 改进的差异算法 - 使用最长公共子序列
    const result: DiffResult[] = [];
    
    // 计算最长公共子序列
    const lcs = computeLCS(processedLeft, processedRight);
    
    let leftIndex = 0;
    let rightIndex = 0;
    let lcsIndex = 0;
    
    while (leftIndex < processedLeft.length || rightIndex < processedRight.length) {
      if (leftIndex < processedLeft.length && rightIndex < processedRight.length && 
          processedLeft[leftIndex] === processedRight[rightIndex]) {
        // 匹配的行
        result.push({
          type: 'unchanged',
          text: leftLines[leftIndex] || '',
          lineNumber: leftIndex + 1
        });
        leftIndex++;
        rightIndex++;
        lcsIndex++;
      } else if (leftIndex < processedLeft.length && 
                 (lcsIndex >= lcs.length || processedLeft[leftIndex] !== lcs[lcsIndex])) {
        // 左侧独有的行
        result.push({
          type: 'removed',
          text: leftLines[leftIndex] || '',
          lineNumber: leftIndex + 1
        });
        leftIndex++;
      } else if (rightIndex < processedRight.length && 
                 (lcsIndex >= lcs.length || processedRight[rightIndex] !== lcs[lcsIndex])) {
        // 右侧独有的行
        result.push({
          type: 'added',
          text: rightLines[rightIndex] || '',
          lineNumber: rightIndex + 1
        });
        rightIndex++;
      } else {
        leftIndex++;
        rightIndex++;
      }
    }

    return result;
  }, [leftContent, rightContent, ignoreWhitespace, ignoreCase]);

  const handleCompare = () => {
    setDiffResult(computeDiff);
  };

  const handleClear = () => {
    setLeftContent('');
    setRightContent('');
    setDiffResult([]);
  };

  const handleSwap = () => {
    setLeftContent(rightContent);
    setRightContent(leftContent);
    setDiffResult([]);
  };

  const handleSampleData = () => {
    const sampleLeft = `function calculateSum(a, b) {
  return a + b;
}

function multiply(x, y) {
  return x * y;
}`;

    const sampleRight = `function calculateSum(a, b) {
  return a + b;
}

function divide(x, y) {
  if (y === 0) {
    throw new Error("Division by zero");
  }
  return x / y;
}`;

    setLeftContent(sampleLeft);
    setRightContent(sampleRight);
    setDiffResult([]);
  };

  const getDiffStats = () => {
    const added = diffResult.filter(item => item.type === 'added').length;
    const removed = diffResult.filter(item => item.type === 'removed').length;
    const unchanged = diffResult.filter(item => item.type === 'unchanged').length;
    
    return { added, removed, unchanged, total: diffResult.length };
  };

  const stats = getDiffStats();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">内容对比工具</h1>
          <p className="text-lg text-gray-600">对比两个文本内容的差异，高亮显示变化部分</p>
        </div>

        {/* Options */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">对比选项</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center">
              <input
                id="showLineNumbers"
                type="checkbox"
                checked={showLineNumbers}
                onChange={(e) => setShowLineNumbers(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="showLineNumbers" className="text-sm text-gray-700">
                显示行号
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                id="ignoreWhitespace"
                type="checkbox"
                checked={ignoreWhitespace}
                onChange={(e) => setIgnoreWhitespace(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="ignoreWhitespace" className="text-sm text-gray-700">
                忽略空白字符
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                id="ignoreCase"
                type="checkbox"
                checked={ignoreCase}
                onChange={(e) => setIgnoreCase(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="ignoreCase" className="text-sm text-gray-700">
                忽略大小写
              </label>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={handleSampleData}
                className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                示例数据
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4 mb-6">
          <button
            onClick={handleCompare}
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            开始对比
          </button>
          <button
            onClick={handleSwap}
            className="px-6 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors font-medium"
          >
            交换内容
          </button>
          <button
            onClick={handleClear}
            className="px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium"
          >
            清空
          </button>
        </div>

        {/* Content Input */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Left Content */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">左侧内容</h2>
            <textarea
              value={leftContent}
              onChange={(e) => setLeftContent(e.target.value)}
              placeholder="输入左侧内容..."
              className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            />
          </div>

          {/* Right Content */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">右侧内容</h2>
            <textarea
              value={rightContent}
              onChange={(e) => setRightContent(e.target.value)}
              placeholder="输入右侧内容..."
              className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            />
          </div>
        </div>

        {/* Diff Results */}
        {diffResult.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">对比结果</h2>
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
                  <span className="text-green-700">新增: {stats.added}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
                  <span className="text-red-700">删除: {stats.removed}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-gray-100 border border-gray-300 rounded"></div>
                  <span className="text-gray-700">未变: {stats.unchanged}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm">
              {diffResult.map((item, index) => (
                <div
                  key={index}
                  className={`py-1 px-2 rounded mb-1 ${
                    item.type === 'added'
                      ? 'bg-green-100 border-l-4 border-green-500'
                      : item.type === 'removed'
                      ? 'bg-red-100 border-l-4 border-red-500'
                      : 'bg-gray-100 border-l-4 border-gray-300'
                  }`}
                >
                  <div className="flex items-start">
                    {showLineNumbers && item.lineNumber && (
                      <span className="text-gray-500 mr-3 min-w-[3rem]">
                        {item.lineNumber}
                      </span>
                    )}
                    <span
                      className={`${
                        item.type === 'added'
                          ? 'text-green-800'
                          : item.type === 'removed'
                          ? 'text-red-800'
                          : 'text-gray-800'
                      }`}
                    >
                      {item.type === 'added' && '+ '}
                      {item.type === 'removed' && '- '}
                      {item.type === 'unchanged' && '  '}
                      {item.text}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">🔍</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">智能对比</h3>
            <p className="text-gray-600">自动识别文本差异，高亮显示变化</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">⚙️</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">灵活配置</h3>
            <p className="text-gray-600">支持多种对比选项和显示设置</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">📊</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">统计信息</h3>
            <p className="text-gray-600">显示新增、删除、未变内容的统计</p>
          </div>
        </div>

        {/* Usage Tips */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-3">使用说明</h3>
          <ul className="text-blue-700 space-y-2 text-sm">
            <li>• 在左右两个文本框中输入要对比的内容</li>
            <li>• 选择对比选项：显示行号、忽略空白字符、忽略大小写</li>
            <li>• 点击"开始对比"查看差异结果</li>
            <li>• 绿色高亮表示新增内容，红色高亮表示删除内容</li>
            <li>• 可以使用"交换内容"快速对比反向差异</li>
            <li>• 支持代码、文本、配置等各种内容格式的对比</li>
            <li>• 点击"示例数据"查看对比效果</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ContentDiff;
